import { useUserService } from "@/hooks/useUserService";
import { type UserTemplateResponse } from "@/services/usersService";
import { getErrorMessage } from "@/utils/errorUtils";
import useSWR from "swr";

const physicianTemplateKey = (physicianId: string) => `/users/${physicianId}/template` as const;

/**
 * Cached physician template (template_id) via SWR.
 * Pass null/undefined physicianId to skip the request.
 */
export function usePhysicianTemplate(physicianId: string | null | undefined) {
  const usersService = useUserService();

  const key = physicianId ? physicianTemplateKey(physicianId) : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<UserTemplateResponse>(
    key,
    () => {
      if (!physicianId) throw new Error("physicianId is required");
      return usersService.getPhysicianTemplate(physicianId);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 30 * 60 * 1000,
    }
  );

  return {
    data,
    isLoading,
    isValidating,
    isError: Boolean(error),
    errorMessage: error ? getErrorMessage(error) : null,
    mutate,
  };
}

export { physicianTemplateKey };
